/**
 * BalloonScene - Balloon popping mini-game
 */

import { useState, useEffect } from 'react';

const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899'];

export default function BalloonScene({ level, isComplete, isListening }) {
  const [balloons, setBalloons] = useState([]);
  const [poppedCount, setPoppedCount] = useState(0);

  // Initialize balloons
  useEffect(() => {
    const newBalloons = [...Array(6)].map((_, i) => ({
      id: i,
      color: COLORS[i],
      x: 15 + (i % 3) * 30,
      y: 20 + Math.floor(i / 3) * 35,
      popped: false,
      delay: i * 0.1,
    }));
    setBalloons(newBalloons);
    setPoppedCount(0);
  }, [level?.id]);

  // Pop a balloon when complete
  useEffect(() => {
    if (isComplete && poppedCount < balloons.length) {
      const interval = setInterval(() => {
        setBalloons(prev => {
          const unpoppedIdx = prev.findIndex(b => !b.popped);
          if (unpoppedIdx === -1) return prev;
          const updated = [...prev];
          updated[unpoppedIdx] = { ...updated[unpoppedIdx], popped: true };
          return updated;
        });
        setPoppedCount(p => p + 1);
      }, 150);
      return () => clearInterval(interval);
    }
  }, [isComplete, poppedCount, balloons.length]);

  return (
    <div style={styles.scene}>
      {/* Sky gradient */}
      <div style={styles.sky} />
      
      {/* Clouds */}
      <div style={{ ...styles.cloud, top: '15%', left: '10%', animationDelay: '0s' }} />
      <div style={{ ...styles.cloud, top: '25%', right: '15%', animationDelay: '2s' }} />
      
      {/* Balloons */}
      {balloons.map(balloon => (
        <div
          key={balloon.id}
          style={{
            ...styles.balloon,
            left: `${balloon.x}%`,
            top: `${balloon.y}%`,
            backgroundColor: balloon.color,
            animationDelay: `${balloon.delay}s`,
            opacity: balloon.popped ? 0 : 1,
            transform: balloon.popped ? 'scale(1.5)' : 'scale(1)',
            transition: 'all 0.3s ease',
          }}
        >
          {/* Balloon shine */}
          <div style={styles.shine} />
          {/* String */}
          <div style={styles.string} />
          {/* Pop particles */}
          {balloon.popped && (
            <div style={styles.popParticles}>
              {[...Array(8)].map((_, i) => (
                <div 
                  key={i} 
                  style={{
                    ...styles.particle,
                    backgroundColor: balloon.color,
                    transform: `rotate(${i * 45}deg) translateY(-30px)`,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      ))}
      
      {/* Grass */}
      <div style={styles.grass} />
      
      {/* Listening indicator */}
      {isListening && (
        <div style={styles.listening}>
          <span style={styles.listeningDot} />
          <span style={{ ...styles.listeningDot, animationDelay: '0.2s' }} />
          <span style={{ ...styles.listeningDot, animationDelay: '0.4s' }} />
        </div>
      )}
    </div>
  );
}

const styles = {
  scene: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
  },
  sky: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, #38bdf8 0%, #7dd3fc 50%, #bae6fd 100%)',
  },
  cloud: {
    position: 'absolute',
    width: '100px',
    height: '40px',
    background: 'white',
    borderRadius: '40px',
    opacity: 0.9,
    animation: 'cloudFloat 8s ease-in-out infinite',
    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
  },
  balloon: {
    position: 'absolute',
    width: '60px',
    height: '75px',
    borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
    animation: 'balloonFloat 3s ease-in-out infinite',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    cursor: 'pointer',
  },
  shine: {
    position: 'absolute',
    top: '15%',
    left: '20%',
    width: '20px',
    height: '20px',
    background: 'rgba(255,255,255,0.5)',
    borderRadius: '50%',
  },
  string: {
    position: 'absolute',
    bottom: '-30px',
    left: '50%',
    width: '2px',
    height: '30px',
    background: '#94a3b8',
    transform: 'translateX(-50%)',
  },
  popParticles: {
    position: 'absolute',
    top: '50%',
    left: '50%',
  },
  particle: {
    position: 'absolute',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    animation: 'particleFly 0.5s ease-out forwards',
  },
  grass: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '15%',
    background: 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)',
  },
  listening: {
    position: 'absolute',
    bottom: '20%',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
  },
  listeningDot: {
    width: '12px',
    height: '12px',
    background: '#22c55e',
    borderRadius: '50%',
    animation: 'dotPulse 1s ease-in-out infinite',
  },
};

// Add keyframes via style tag
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes balloonFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-15px); }
  }
  @keyframes cloudFloat {
    0%, 100% { transform: translateX(0); }
    50% { transform: translateX(20px); }
  }
  @keyframes particleFly {
    0% { opacity: 1; transform: rotate(var(--r, 0deg)) translateY(0); }
    100% { opacity: 0; transform: rotate(var(--r, 0deg)) translateY(-50px); }
  }
  @keyframes dotPulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.3); opacity: 0.7; }
  }
`;
if (!document.querySelector('#balloon-scene-styles')) {
  styleSheet.id = 'balloon-scene-styles';
  document.head.appendChild(styleSheet);
}
